import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GatedAssetForm } from '@/components/forms/GatedAssetForm';
import { ArrowRight, BookOpen, Download, MessageCircle, PlayCircle } from 'lucide-react';
import { trackEvent, trackDiscoveryCallClick } from '@/utils/analytics';
import { handleDiscoveryCallClick } from '@/components/layout/Navbar';

interface CommitmentLadderProps {
  context: 'homepage' | 'certification' | 'general';
}

export const CommitmentLadder: React.FC<CommitmentLadderProps> = ({ context }) => {
  const [activeForm, setActiveForm] = useState<'medium' | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const { links } = useSiteSettings();

  useEffect(() => {
    if (activeForm && formRef.current) {
      // Allow for sticky header (typically ~80px to 96px)
      const headerOffset = 96;
      const elementPosition = formRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });

      // Focus the first input field after smooth scroll begins
      const timer = setTimeout(() => {
        const firstInput = formRef.current?.querySelector<HTMLInputElement>(
          'input:not([type="hidden"]):not([style*="display: none"])'
        );
        firstInput?.focus();
      }, 450);

      return () => clearTimeout(timer);
    }
  }, [activeForm]);

  const renderForm = () => {
    if (activeForm === 'medium') {
      return (
        <div 
          ref={formRef} 
          className="mt-8 p-6 sm:p-8 bg-white border border-ground-deep relative animate-in fade-in slide-in-from-top-4 duration-300 mb-16 lg:mb-8 shadow-sm scroll-mt-28"
        >
          <button 
            onClick={() => setActiveForm(null)}
            className="absolute top-4 right-4 text-ink/50 hover:text-rule-3 text-sm font-bold uppercase tracking-widest"
          >
            Close
          </button>
          <div className="max-w-xl mx-auto text-center mb-8">
            <h3 className="text-2xl font-serif text-ink mb-3">
              {context === 'certification' ? 'Download the Curriculum' : 'Download a Sample Report'}
            </h3>
            <p className="text-ink/70">
              {context === 'certification' 
                ? 'Get the complete syllabus, module breakdown, and ICF accreditation details.' 
                : 'See exactly what insights the FIVE LENS Enneagram delivers for individuals and teams.'}
            </p>
          </div>
          <GatedAssetForm 
            assetName={context === 'certification' ? 'Curriculum PDF' : 'Sample Report'}
            buttonText={context === 'certification' ? 'Send me the Curriculum' : 'Send me the Sample Report'}
            eventName={context === 'certification' ? 'certificate_brochure_download_submit' : 'sample_report_request'}
            ctaPosition="commitment_ladder_medium"
          />
        </div>
      );
    }

    return null;
  };

  return (
    <section className="py-20 pb-36 lg:pb-20 bg-ground-alt border-t border-ground-deep">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-serif text-ink mb-6">
            Take the next step
          </h2>
          <p className="text-lg text-ink/70">
            Whether you are ready to begin, or just starting your research, we have the resources you need.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          
          {/* High Intent */}
          <div className="bg-white border border-ground-deep p-8 lg:p-10 flex flex-col items-start hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-rule-3/20 rounded-full flex items-center justify-center mb-6">
              <MessageCircle className="w-5 h-5 text-rule-3" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-rule-3 mb-3">Ready to act</span>
            <h3 className="text-xl font-serif text-ink mb-4">Let's Talk</h3>
            <p className="text-ink/70 text-sm mb-8 flex-1">
              Speak with a senior consultant to discuss your specific context and requirements. No obligation.
            </p>
            <div className="w-full space-y-3">
              <a 
                href={links.bookingUrl || 'https://outlook.office.com/book/Schedulea20minuteDiscoverySession@enneainternational.com/?ismsaljsauthenabled'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  const url = links.bookingUrl || 'https://outlook.office.com/book/Schedulea20minuteDiscoverySession@enneainternational.com/?ismsaljsauthenabled';
                  handleDiscoveryCallClick(url);
                  trackDiscoveryCallClick('commitment_ladder_high');
                }}
                className="w-full flex items-center justify-between px-6 py-4 bg-ink text-white text-xs font-bold uppercase tracking-widest hover:bg-rule-3 transition-colors"
              >
                Book a Call <ArrowRight className="w-4 h-4" />
              </a>
              {context === 'certification' && (
                <a 
                  href="/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('ladder_click_register', { cta_position: 'commitment_ladder_high' })}
                  className="w-full flex items-center justify-between px-6 py-4 bg-white border border-ink text-ink text-xs font-bold uppercase tracking-widest hover:bg-ground-alt transition-colors"
                >
                  Register Now <ArrowRight className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Medium Intent */}
          <div className="bg-white border border-ground-deep p-8 lg:p-10 flex flex-col items-start hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-ink/5 rounded-full flex items-center justify-center mb-6">
              <BookOpen className="w-5 h-5 text-ink" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink/50 mb-3">Researching</span>
            <h3 className="text-xl font-serif text-ink mb-4">
              {context === 'certification' ? 'Get the Curriculum' : 'See a Sample Report'}
            </h3>
            <p className="text-ink/70 text-sm mb-8 flex-1">
              {context === 'certification' 
                ? 'Review the syllabus, dates, and exactly what you will learn.' 
                : 'Download a complete sample report to see the depth of our insights.'}
            </p>
            <button 
              onClick={() => setActiveForm(activeForm === 'medium' ? null : 'medium')}
              className={`w-full flex items-center justify-between px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors ${activeForm === 'medium' ? 'bg-rule-3 text-white' : 'bg-white border border-ground-deep text-ink hover:border-ink'}`}
            >
              {context === 'certification' ? 'Get the curriculum' : 'Get the sample report'} <Download className="w-4 h-4" />
            </button>
          </div>

          {/* Low Intent */}
          <div className="bg-white border border-ground-deep p-8 lg:p-10 flex flex-col items-start hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-ink/5 rounded-full flex items-center justify-center mb-6">
              <PlayCircle className="w-5 h-5 text-ink" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink/50 mb-3">Exploring</span>
            <h3 className="text-xl font-serif text-ink mb-4">Try a Sample</h3>
            <p className="text-ink/70 text-sm mb-8 flex-1">
              Answer 24 quick questions and see the pattern you lean on, where you go first, and a resilience snapshot. About 4 minutes.
            </p>
            <Link 
              to="/mini-assessment"
              className="w-full flex items-center justify-between px-6 py-4 text-xs font-bold uppercase tracking-widest transition-colors bg-white border border-ground-deep text-ink hover:border-ink hover:bg-ground-alt"
            >
              Start Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>

        {renderForm()}

      </div>
    </section>
  );
};
